import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/ClerkAuthContext';
import { apiClient, useApiClient } from '../api/client';

export function DevboxAuthPage() {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // IMPORTANT: Set up the API client with auth token
  useApiClient();

  useEffect(() => {
    handleDevboxAuth();
  }, [user]);

  const handleDevboxAuth = async () => {
    try {
      const params = new URLSearchParams(window.location.search);

      // Check for new base64-encoded auth parameter
      const authParam = params.get('auth');
      let devboxURL: string | null = null;
      let returnPath = '/';

      if (authParam) {
        try {
          // Decode base64 and parse JSON
          const decoded = atob(authParam);
          const authData = JSON.parse(decoded);
          devboxURL = authData.devbox;
          returnPath = authData.return || '/';
        } catch (e) {
          console.error('Failed to decode auth parameter:', e);
          setError('Invalid authentication request: malformed auth parameter');
          setIsRedirecting(false);
          return;
        }
      } else {
        // Fall back to legacy URL parameters
        devboxURL = params.get('devbox');
        returnPath = params.get('return') || '/';
      }

      if (!devboxURL) {
        setError('Invalid authentication request: missing devbox URL');
        setIsRedirecting(false);
        return;
      }

      // No organization check needed for slug-based auth - devbox access is controlled by slug permissions

      // Get devbox token from backend
      const response = await apiClient.post<{ token: string; expires_in: number }>(
        '/api/org/devbox-token',
        { devbox_url: devboxURL }
      );

      if (!response.token) {
        throw new Error('Failed to generate authentication token');
      }

      // Set redirecting state to prevent rendering
      setIsRedirecting(true);

      // Redirect back to devbox with token
      const callbackURL = new URL('/__vibe/auth/callback', devboxURL);
      callbackURL.searchParams.set('token', response.token);
      callbackURL.searchParams.set('return', returnPath);

      // Redirect to devbox - using replace to avoid back button issues
      window.location.replace(callbackURL.toString());
    } catch (error) {
      console.error('Authentication failed:', error);
      setError(error instanceof Error ? error.message : 'Authentication failed');
      setIsRedirecting(false);
    }
  };

  // Only render if there's an error - otherwise redirect is in progress
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Authentication Error
            </h2>
            <div className="mt-4 p-4 bg-red-50 rounded-md">
              <p className="text-red-800">{error}</p>
            </div>
            <div className="mt-6">
              <a
                href="/"
                className="text-indigo-600 hover:text-indigo-500"
              >
                Return to Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render anything while redirecting - prevents flash
  return null;
}